import { Type } from '@angular/core';

import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import moment from 'moment-timezone';
import { CircleBullet, ColumnSeries, DateAxis, IXYSeriesDataFields, LineSeries, ValueAxis, XYChart, XYSeries } from '@amcharts/amcharts4/charts';

import { chartColors } from '../enums/chart-colors.enum';
import { ChartMetricsEnum, chartMetricsToDateAndValueXYChartUnitMap } from '../../Reports/enums/chart-metrics.enum';
import { ChartModel } from './chart.model';
import { ChartSpecificTypesEnum } from '../../Reports/enums/chart-specific-types.enum';
import {
    chartSpecificTypesToBaseAxisDirectionsMap,
    chartSpecificTypesToValueAxisDirectionsMap,
    ReportBlockTypeEnum
} from '../../Reports/enums/report-block-type.enum';
import { ChartSeries } from './chart-series.model';
import { ChartSizeHelper } from '../helpers/chart-size.helper';
import { currencySymbolMap } from '../../../Core/Users/enums/user-currency';
import { DataPointModel } from './data-point.model';
import { DateAndValueXYChartOptionsModel } from './date-and-value-x-y-chart-options.model';
import { DateAndValueXYChartUnitEnum } from '../enums/date-and-value-x-y-chart-unit.enum';
import { DateQueryModel } from '../../Forms/models/date-query.model';
import { DateQueryTypeEnum } from '../../../api/mentions/enum/date-query-type.enum';
import { defaultSeriesColorConfig } from '../config/default-series-color.config';
import { manualChartExport } from '../helpers/manualChartExport.helper';
import { MentionSearchCriteriaModel } from '../../../Monitor/Shared/models/mention-search-criteria.model';
import { ValueAxisModel } from './axis.model';
import { ProminenceRecalculationJobStatus } from '../../../api/monitoring/monitoring-account-configuration.api';
import { CustomLegendItem } from './custom-legend-item.model';
import { SeriesModel } from './series.model';
import { AbstractChartOptionsModel } from './abstract-chart-options.model';
import { ChartMetadataModel } from './chart-metadata.model';
import { StackedChartTypesEnum } from '../../../Common/Reports/enums/stacked-chart-types.enum';
import { safeDivide } from '../../../Core/utils';


export class DateValueXYChart extends ChartModel {

    declare amchart: XYChart;
    declare options: DateAndValueXYChartOptionsModel;

    dateAxis: DateAxis;
    valueAxis: ValueAxis;
    valueAxes: ValueAxis[] = [];
    groupDataPoints: DataPointModel[] = [];
    bullets: CircleBullet[] = [];
    isSingleSeries: boolean;

    readonly chartType: Type<XYChart> = XYChart;
    readonly exportTypes: (keyof am4core.IExportOptions)[] = ['json', 'csv'];

    constructor(
        series: SeriesModel[],
        options: AbstractChartOptionsModel = {},
        metadata?: ChartMetadataModel,
        chartSeries?: ChartSeries[]
    ) {
        super(series, options, metadata);
        this.chartSeries = chartSeries;
    }

    public initChart(element: HTMLElement, chartType = this.chartType) {

        super.initChart(element, chartType);


        this.isSingleSeries = this.chartSeries.length === 1;
        if (this.options.extraPaddingTop) {
            this.amchart.paddingTop = this.options.extraPaddingTop;
        }

        this.amchart.numberFormatter.bigNumberPrefixes = [
            { 'number': 1e+3, 'suffix': 'K' },
            { 'number': 1e+6, 'suffix': 'M' },
            { 'number': 1e+9, 'suffix': 'B' },
            { 'number': 1e+12, 'suffix': 'T' },
            { 'number': 1e+15, 'suffix': 'Q' }
        ];
        // intentionally set smallNumberPrefixes to empty array so that it will not transform small numbers with wired prefix like 'm' or 'mu' etc.
        this.amchart.numberFormatter.smallNumberPrefixes = [];

        this.setUpDateAxis();
        this.setUpValueAxes();
        this.setUpCursor();
        this.setUpSeries();
        this.setUpLegend();
        this.setUpScrollbar();
        this.setUpResponsiveness();
        this.handleDateAxisRange();
        this.handleValueAxisRange();
        this.handleSparkline();

        if (this.options.doReadyInInit) {
            this.ready.next(true);
            this.resetZoomOut();
        }
    }

    setUpDateAxis(): void {
        const axis = this.amchart[chartSpecificTypesToBaseAxisDirectionsMap.get(this.options.chartSpecificType)];
        this.dateAxis = axis.push(new DateAxis());
        this.dateAxis.cursorTooltipEnabled = false;
        if (this.options.isStacked) {
            this.dateAxis.renderer.minGridDistance = 60;
            this.dateAxis.startLocation = 0.5;
            this.dateAxis.endLocation = 0.6;
            this.dateAxis.gridIntervals.setAll([
                { timeUnit: 'minute', count: 5 },
                { timeUnit: 'minute', count: 15 },
                { timeUnit: 'minute', count: 30 },
                { timeUnit: 'hour', count: 1 },
                { timeUnit: 'hour', count: 2 },
                { timeUnit: 'hour', count: 4 },
                { timeUnit: 'day', count: 1 },
                { timeUnit: 'day', count: 2 },
                { timeUnit: 'day', count: 7 },
                { timeUnit: 'month', count: 1 },
                { timeUnit: 'month', count: 3 },
                { timeUnit: 'year', count: 1 }
            ]);

            this.dateAxis.renderer.ticks.template.stroke = am4core.color('#ced4da');
            this.dateAxis.renderer.ticks.template.length = 6;
        } else {
            this.dateAxis.renderer.minGridDistance = 50;
            this.dateAxis.renderer.ticks.template.stroke = am4core.color(chartColors.agprText);
            this.dateAxis.renderer.ticks.template.length = 8;
        }
        this.dateAxis.renderer.ticks.template.disabled = false;
        this.dateAxis.renderer.ticks.template.strokeOpacity = 1;
        this.dateAxis.renderer.ticks.template.strokeWidth = 1;

        this.dateAxis.groupData = true;
        this.dateAxis.groupCount = 36;

        this.dateAxis.groupIntervals.setAll([
            { timeUnit: 'hour', count: 1 },
            { timeUnit: 'day', count: 1 },
            { timeUnit: 'week', count: 1 },
            { timeUnit: 'month', count: 1 },
            { timeUnit: 'month', count: 3 },
            { timeUnit: 'year', count: 1 }
        ]);
        if (this.options.groupIntervals) {
            this.dateAxis.groupInterval = this.options.groupIntervals[0];
        }
        if (this.options.dateGranularity) {
            this.dateAxis.baseInterval = this.options.dateGranularity;
        }
    }

    setUpValueAxes(): void {
        // HACK: drilldown, when you open the drilldown / or change source, it runs setUpValueAxes again, and the valueAxes array keeps growing
        // and with that, the value axis dissapears (because of this.valueAxes.push(axis))
        this.valueAxes = [];
        this.chartSeries.forEach((series, index) => {
            if (series.requiresAxis) {
                const valueAxisModel: ValueAxisModel = {
                    key: `series_${index}`,
                    label: series.axisTitleText,
                    valueLabel: this.options.yValueLabel,
                    unit: chartMetricsToDateAndValueXYChartUnitMap.get((series.key) as ChartMetricsEnum),
                    percentLabel: this.options.percentLabel,
                    color: this.isSingleSeries ? null : this.styles.colorMap[`analysisColor${index + 1}`],
                    position: index === 0  ? 'left' : 'right',
                    hideGrid: series.hideAxisGrid,
                    percentageValueAxis: series.percentageValueAxis
                };
                const axis = this.addValueAxis(valueAxisModel, this.valueAxes[0]);
                this.valueAxes.push(axis);
            }
        });
        // todo: used in coverage impact trend right axis, broken now, should investigate
        // if (this.options.yAxes?.length > 0) {
        //     this.valueAxesConfig.push(...this.options.yAxes);
        //     this.options.yAxes.forEach(yAxis => {
        //         this.valueAxes.push(
        //             this.addYAxis(yAxis, this.defaultValueAxis)
        //         );
        //     });
        // }
        if (this.isSingleSeries && this.options.maxYValue) {
            this.valueAxes[0].max = this.options.maxYValue;
        }
    }

    setUpCursor(){
        if (this.options.useAmchartCursor) {
            this.amchart.cursor = new am4charts.XYCursor();
            this.amchart.cursor.maxTooltipDistance = 0;
            this.amchart.cursor.lineY.disabled = true;
            this.amchart.cursor.lineX.stroke = am4core.color('#ffffff');
            this.amchart.cursor.lineX.strokeWidth = 1;
            this.amchart.cursor.lineX.strokeOpacity = 1;
        }
    }

    addValueAxis(valueAxisModel: ValueAxisModel, syncAxis?: ValueAxis): ValueAxis {
        const valueAxisDirection = this.amchart[chartSpecificTypesToValueAxisDirectionsMap.get(this.options.chartSpecificType)];
        const axis = valueAxisDirection.push(new ValueAxis());
        axis.cursorTooltipEnabled = false;

        axis.title.text = valueAxisModel.label;

        if (this.options.isStacked) {
            axis.numberFormatter = new am4core.NumberFormatter();
            axis.numberFormatter.numberFormat = '#a';
            if (this.metadata.stackedChartTypes === StackedChartTypesEnum.PERCENTAGE) {
                axis.calculateTotals = true;
                axis.max = 100;
                axis.strictMinMax = true;
            } else {
                // Make the top on the axis 10% higher than the highest actual value
                axis.extraMax = 0.1;
            }
        }

        if (valueAxisModel.percentageValueAxis) {
            axis.renderer.labels.template.adapter.add('text', function (text) {
                if (text) {
                    if (!text.endsWith('%')) {
                        return text + '%';
                    }
                    return text;
                }
                return '';
            });
        }

        axis.min = 0;
        // Only show integer values
        axis.maxPrecision = 0;
        axis.minGridDistance = 40;
        if (valueAxisModel.position === 'right') {
            this.amchart.rightAxesContainer.reverseOrder = true;
            axis.renderer.opposite = true;
        }
        if (syncAxis) {
            axis.syncWithAxis = syncAxis;
            // Adds some values to the chart so that it syncs
            if (syncAxis.max % 2 !== 0) {
                axis.extraMax = 0.125;
            }
        }

        if (valueAxisModel.unit === DateAndValueXYChartUnitEnum.CURRENCY) {
            axis.numberFormatter = new am4core.NumberFormatter();
            const currency = this.options.styles.currency;
            if (currency) {
                const currencySymbol = currencySymbolMap.get(currency);
                if (currencySymbol) {
                    axis.numberFormatter.numberFormat = `${currencySymbol}#,###.#a`;
                } else {
                    axis.numberFormatter.numberFormat = `#,###.#a ${currency}`;
                }
            } else {
                axis.numberFormatter.numberFormat = '#,###.#a';
            }
        } else if (this.options.unit === DateAndValueXYChartUnitEnum.PROMINENCE_LEVELS) {
            // Using value instead of categories so that the points themselves
            // will retain float precision, it's only the axis that needs to be categorized.
            // Note that the axis categories don't exactly reflect the thresholds (see ProminenceScoreModel)
            axis.extraMax = 0;
            axis.max = 5;
            const promLabels = this.options.prominenceLabels;
            axis.renderer.labels.template.adapter.add('textOutput', function(text, target) {
                return promLabels[+text];
            });
        } else {
            axis.numberFormatter.numberFormat = '#.#a';
        }

        if (valueAxisModel.color) {
            axis.renderer.line.strokeOpacity = 1;
            axis.renderer.line.strokeWidth = 2;
            axis.renderer.line.stroke = am4core.color(valueAxisModel.color);
        }

        if (valueAxisModel.hideGrid) {
            axis.renderer.grid.template.disabled = true;
        }

        return axis;
    }

    setUpSeries(): void {
        this.chartSeries.forEach((series, index) => {
            this.addSeries(index, series);
        });
    }

    addSeries(
        seriesIndex: number,
        chartSeries: ChartSeries,
    ): XYSeries {
        const metricType = chartSeries.key as ChartMetricsEnum;
        let color = this.styles?.colorMap[chartSeries.key]
            || this.styles?.colorMap[`seriesColor${seriesIndex}`]
            || defaultSeriesColorConfig[seriesIndex % defaultSeriesColorConfig.length];
        if (metricType) {
            color = this.styles.colorMap[`analysisColor${seriesIndex + 1}`] || color;
        };

        let series;
        switch (chartSeries.chartSpecificType) {
            case ChartSpecificTypesEnum.LINE:
            case ChartSpecificTypesEnum.FILLED_LINE:
            case ChartSpecificTypesEnum.MULTI_LINE:
                // MULTI_LINE draws as a line too: the multi is the series list this chart is given,
                // not a different kind of series
                series = this.amchart.series.push(new LineSeries());
                this.setUpLineSeries(series, color, seriesIndex, metricType, chartSeries);
                break;
            case ChartSpecificTypesEnum.COLUMN:
                series = this.amchart.series.push(new ColumnSeries());
                this.setUpColumnSeries(series, color, seriesIndex, metricType);
                break;
        }

        // Nothing drawn for a type this chart has no series for, rather than failing on the way out
        if (!series) {
            return null;
        }

        series.name = chartSeries.label;
        series.yAxis = this.valueAxes[chartSeries.axisIndex];

        if (this.metadata.stackedChartTypes === StackedChartTypesEnum.PERCENTAGE) {
            series.dataFields.valueYShow = 'totalPercent';
        }


        const dataFields: IXYSeriesDataFields = series.dataFields;
        dataFields.dateX = 'key';

        const yValueIdentifier = 'value' + seriesIndex;
        const yPercentIdentifier = 'percent' + seriesIndex;
        dataFields.valueY = yValueIdentifier;

        if (this.options.responsive) {
            const formatUnit = chartMetricsToDateAndValueXYChartUnitMap.get(metricType) || this.options.unit;
            const tooltipLabelDataIdentifier = this.series?.length === 0 ? 'label' : 'name';
            if (formatUnit === DateAndValueXYChartUnitEnum.NUMBER) {
                series.tooltipText = `{${tooltipLabelDataIdentifier}} - {${yValueIdentifier}}`;
            } else if (formatUnit === DateAndValueXYChartUnitEnum.CURRENCY) {
                series.tooltipText = `{${tooltipLabelDataIdentifier}} - {${yValueIdentifier}.formatNumber('#,###.00a')}`;
            } else {
                if (this.metadata.stackedChartTypes === StackedChartTypesEnum.PERCENTAGE) {
                    series.tooltipText = `{${tooltipLabelDataIdentifier}} - {${yPercentIdentifier}}`;
                } else {
                    series.tooltipText = `{${tooltipLabelDataIdentifier}} - {${yValueIdentifier}}`;
                }
            }
            series.tooltip.getFillFromObject = false;
            series.tooltip.background.fill = am4core.color(series.stroke.toString());
        }

        this.groupDataItem(series, yValueIdentifier, yPercentIdentifier, seriesIndex, chartSeries);

        return series;
    }

    setUpLineSeries(
        series: LineSeries,
        color: string,
        seriesIndex: number,
        metricType: ChartMetricsEnum,
        chartSeries: ChartSeries
    ): void {
        series.tooltip.events.on('positionchanged', event => {
            event.target.fitPointerToBounds = true;
        });
        this.amchart.events.on('sizechanged', event => {
            if (this.amchart.pixelWidth < 500 && !this.options.isSocialStyles) {
                series.tooltip.pointerOrientation = 'left';
                this.dateAxis.renderer.maxLabelPosition = 1; // Don't let labels appear within 5 percent of the end of the dateAxis
            } else {
                series.tooltip.pointerOrientation = 'horizontal';
            }
        });

        if (this.options.isStacked) {
            series.tooltip.getFillFromObject = false;
            series.tooltip.background.fill = am4core.color(chartColors.agprWhite);
            series.tooltip.background.stroke = am4core.color('#ced4da');
            series.tooltip.background.cornerRadius = 8;
            series.tooltip.background.fillOpacity = 1;
            // clear tooltip background shadow
            series.tooltip.background.filters.clear();
            series.tooltip.label.fill = am4core.color(chartColors.agprBlack);
            series.tooltip.label.interactionsEnabled = true;
            series.tooltip.keepTargetHover = true;

            series.stacked = true;
            series.fill = am4core.color(color);
            series.strokeWidth = 1;
            series.stroke = am4core.color(color);
            series.fillOpacity = 0.9;
            series.tensionX = 0.78;
            series.tooltip.animationDuration = 400;
        } else {
            series.strokeWidth = 2;
            series.stroke = am4core.color(color);
        }
        series.cursorTooltipEnabled = false;

        this.setUpBullets(series, color, seriesIndex, metricType);

        if (chartSeries.strokeOpacity) {
            series.strokeOpacity = chartSeries.strokeOpacity;
        }
        if (chartSeries.strokeDasharray) {
            series.strokeDasharray = chartSeries.strokeDasharray as string;
        }
        // Makes the line curved
        if (!this.options.notCurved) {
            series.tensionX = 0.8;
            series.tensionY = 0.9;
        }
        // hide bullet points when the distance between two bullet points is less than 10px
        if (this.options.responsive) {
            series.minBulletDistance = 10;
        }

        // set filled line
        if (this.options.chartSpecificType === ChartSpecificTypesEnum.FILLED_LINE) {
            series.fillOpacity = 1;
            series.fill = am4core.color(color);

            const fillModifier = new am4core.LinearGradientModifier();
            fillModifier.opacities = [1, 0];
            fillModifier.offsets = [0, 1];
            fillModifier.gradient.rotation = 90;
            series.segments.template.fillModifier = fillModifier;
        }

        this.dateAxis.renderer.grid.template.disabled = true;
    }

    setUpBullets(
        series: LineSeries,
        color: string,
        seriesIndex: number,
        metricType: ChartMetricsEnum
    ): void {
        const bullet: CircleBullet = series.bullets.push(new CircleBullet());
        const bulletHover = bullet.states.create('hover');
        if (this.options.hiddenBullets) {
            bullet.circle.strokeOpacity = 0;
            bullet.circle.fillOpacity = 0;

        } else {
            if (!this.options?.ignoreBullets) {
                bullet.circle.strokeWidth = 1;
                bullet.circle.radius = 3;
                bullet.circle.fill = am4core.color('#fff');
                bullet.circle.stroke = am4core.color(color);
            } else {
                bullet.circle.strokeWidth = 5;
                bullet.circle.radius = 1;
                bullet.circle.fill = am4core.color(color);
                bullet.circle.strokeOpacity = 0;
            }

            // todo: store seriesIndex not with id; id might mean differently according to chart types. Need to do this after all date charts use this new model
            bullet.propertyFields.id = '' + seriesIndex || ''; // @HACK see ReportsChart - nick
            bullet.propertyFields.disabled = 'bulletDisabled';
            bullet.propertyFields['metric'] = metricType;
            bullet.hoverOnFocus = true;
            bullet.focusable = true;
            bullet.tooltip = new am4core.Tooltip();
            bullet.tooltip.reverseOrder = true;

            bulletHover.properties.scale = 1.3;

            if (this.options.isSparkline) {
                series.strokeWidth = 2;
                bullet.circle.radius = 3;
                bullet.circle.strokeWidth = 2;
                bullet.tooltip.disabled = true;
                bullet.focusable = false;
            }

            // set tooltip style
            bullet.tooltip.getFillFromObject = false;
            bullet.tooltip.fill = am4core.color(color);
            bullet.tooltip.background.fill = am4core.color(chartColors.agprWhite);
            bullet.tooltip.background.stroke = am4core.color('#ced4da');
            bullet.tooltip.background.cornerRadius = 8;
            bullet.tooltip.background.fillOpacity = 1;
            bullet.tooltip.background.filters.clear();
            bullet.tooltip.label.padding(0, 0, 0, 0);
            bullet.tooltip.label.fill = am4core.color(chartColors.agprBlack);
            bullet.tooltip.label.interactionsEnabled = true;
            bullet.tooltip.keepTargetHover = true;
            // horizontal pointer orientation isn't smart enough to change the position of tooltip (left or right) based on the bullet point position
            // need to override tooltip's bounding container so the tooltip can always show inside the chart container
            bullet.tooltip.boundingContainer = this.amchart;
            bullet.tooltip.maxWidth = 275;
            bullet.tooltip.label.wrap = true;
            bullet.tooltip.ignoreBounds = true;
        }

        this.bullets.push(bullet);
    }

    setUpColumnSeries(
        series: ColumnSeries,
        color: string,
        seriesIndex: number,
        metricType: ChartMetricsEnum
    ): void {
        series.stroke = am4core.color(color);

        this.dateAxis.renderer.cellStartLocation = 0.1;
        this.dateAxis.renderer.cellEndLocation = 0.9;

        const columnTemplate = series.columns.template;
        columnTemplate.fill = am4core.color(color);
        columnTemplate.stroke = am4core.color(color);
        columnTemplate.width = am4core.percent(100);
        columnTemplate.hoverOnFocus = true;
        columnTemplate.focusable = true;

        // set tooltip style
        columnTemplate.tooltip = new am4core.Tooltip();
        columnTemplate.tooltipHTML = '';
        columnTemplate.tooltip.getFillFromObject = false;
        columnTemplate.tooltip.label.padding(0, 0, 0, 0);
        columnTemplate.tooltip.label.fill = am4core.color(chartColors.agprBlack);
        columnTemplate.tooltip.label.interactionsEnabled = true;
        columnTemplate.tooltip.label.maxWidth = 300;
        columnTemplate.tooltip.label.wrap = true;
        // columnTemplate.tooltip.background.filters.clear();
        columnTemplate.tooltip.background.stroke = am4core.color('#ced4da');
        columnTemplate.tooltip.background.cornerRadius = 8;
        columnTemplate.tooltip.background.fill = am4core.color(chartColors.agprWhite);
        columnTemplate.tooltip.background.fillOpacity = 1;
        columnTemplate.tooltip.keepTargetHover = true;

        columnTemplate.propertyFields.id = '' + seriesIndex || ''; // @HACK see ReportsChart - nick
        columnTemplate.propertyFields['metric'] = metricType;

        this.dateAxis.renderer.grid.template.disabled = false;

        // Text to use as aria-label for each column
        let srText = '{label}, ';
        if (metricType === ChartMetricsEnum.ave) {
            const currency = this.options.styles.currency;
            if (currency) {
                const currencySymbol = currencySymbolMap.get(currency);
                if (currencySymbol) {
                    srText += `{value${seriesIndex}.formatNumber('${ currencySymbol }#.#a')}`;
                } else {
                    srText += `{value${seriesIndex}.formatNumber('#.#a ${ currency }')}`;
                }
            }
        } else {
            srText += `{value${seriesIndex}.formatNumber(\'#.#a\')}`;
        }

        series.itemReaderText = srText;
    }

    groupDataItem(
        series: XYSeries,
        yValueIdentifier: string,
        yPercentIdentifier: string,
        seriesIndex: number,
        chartSeries: ChartSeries
    ): void {
        series.adapter.add('groupDataItem', (val) => {
            let mentionCount = 0;
            val.value = 0;
            for (let i = 0; i < val.dataItem.groupDataItems.length; i++) {
                const item = val.dataItem.groupDataItems[i];
                val.value += item.valueY;
                // for prominence over time chart, yValue is average prominence instead of mentions
                // thus we also calculate the sum of mentionCount
                mentionCount += +item.dataContext['mentionCount'];
            }

            // for prominence chart, when grouping data items, it should use average value instead of sum value
            if (this.options.unit === DateAndValueXYChartUnitEnum.PROMINENCE_LEVELS && val.dataItem.groupDataItems.length > 0) {
                val.value = +val.value / +val.dataItem.groupDataItems.length;
            }

            val.dataItem.dataContext[yValueIdentifier] = val.value;
            val.dataItem.dataContext[yPercentIdentifier] = parseFloat((safeDivide(val.value, mentionCount) * 100).toFixed(1));
            val.dataItem.dataContext['mentionCount'] = mentionCount;

            if ((this.options as DateAndValueXYChartOptionsModel).tooltipHTML && val.dataItem.groupDataItems?.length > 1) {
                // when serveral data points are grouped together as one data bullet,
                // we need to reset the date range in view mentions url to include all date range
                const items = val.dataItem.groupDataItems;
                const startDate = new Date((items[0].dataContext as DataPointModel).rawDateFacetFrom );
                const endDate = new Date((items[items.length - 1].dataContext as DataPointModel).rawDateFacetTo);
                const dateRange = new DateQueryModel();
                dateRange.type = DateQueryTypeEnum.ABSOLUTE;
                dateRange.absoluteRange = {
                    start: startDate,
                    end: endDate
                };
                const filters = new MentionSearchCriteriaModel();
                filters.query.dateQuery = DateQueryModel.makeAbsoluteRange(dateRange);
                val.dataItem.dataContext['viewMentionUrl' + seriesIndex] = chartSeries.getSeriesUrl(filters);
                val.dataItem.dataContext['dateRangeLabel'] = moment(startDate).format('YYYY/MM/DD') + ' - ' + moment(endDate).format('YYYY/MM/DD');
            }

            if (val.dataItem.groupDataItems?.length > 1) {
                const items = val.dataItem.groupDataItems;
                const first = items[0].dataContext as DataPointModel;
                const last = items[items.length - 1].dataContext as DataPointModel;
                const startDate: string = moment(first.rawStartDate).format('ddd MMM D YYYY');
                const endDate: string = moment(last.rawEndDate).format('ddd MMM D YYYY');

                const label = `${startDate} - ${endDate}`;
                val.dataItem.dataContext['label'] = label;
                let groupDataPoint;
                const index = this.groupDataPoints.findIndex(x => x.label === label);
                if (index > -1) {
                    groupDataPoint = this.groupDataPoints[index];
                    groupDataPoint[yValueIdentifier] = val.value;
                    val.dataItem.dataContext[yPercentIdentifier] = parseFloat((safeDivide(val.value, mentionCount) * 100).toFixed(1));

                } else {
                    groupDataPoint = {
                        label,
                        startTimeExport: first.startTimeExport,
                        endTimeExport: last.endTimeExport
                    };
                    groupDataPoint[yValueIdentifier] = val.value;
                    val.dataItem.dataContext[yPercentIdentifier] = parseFloat((safeDivide(val.value, mentionCount) * 100).toFixed(1));

                    this.groupDataPoints.push(groupDataPoint);
                }
            }

            return val;
        });
    }

    setUpLegend(): void {
        if (this.options.isSparkline) {
            return;
        }
        if (this.options.legendContainerElement ) {
            if ( this.getCustomLegendItems().length > 1 || this.options.displayLegend ) {
                this.initCustomLegend();
            }
            return;
        } else {
            this.amchart.legend = new am4charts.Legend();
            const legendItemWidth = this.options.styles['legendItemWidth'];
            if (legendItemWidth && this.amchart.legend) {
                this.amchart.legend.itemContainers.template.width = legendItemWidth;
                this.amchart.legend.labels.template.truncate = true;
            }
            if (this.options.squareLegend) {
                this.amchart.legend.itemContainers.template.togglable = true;
                this.amchart.legend.fontSize = '0.875rem';
                this.amchart.legend.fontSize = 12;
                this.amchart.legend.markers.template.scale = 0.75;
                this.amchart.legend.itemContainers.template.width = 100;
                this.amchart.legend.itemContainers.template.paddingLeft = 12;
                this.amchart.legend.reverseOrder = true;
                if (this.options.isSocialStyles) {
                    this.amchart.legend.fontSize = '0.8rem';
                    this.amchart.legend.paddingTop = 10;
                    this.amchart.legend.markers.template.width = 20;
                    this.amchart.legend.markers.template.height = 20;
                    this.amchart.legend.useDefaultMarker = true;
                    // Modify marker template to have rounded corners
                    const marker = this.amchart.legend.markers.template.children.getIndex(0) as am4core.RoundedRectangle;
                    marker.cornerRadius(3, 3, 3, 3);
                }
            } else {
                if (this.metadata.type === ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON || this.amchart.series.length > 1) {
                    this.amchart.legend.scrollable = true;
                    this.amchart.legend.contentAlign = 'left';
                    this.amchart.legend.valign = 'bottom';
                    this.amchart.legend.fontSize = 12;
                    this.amchart.legend.labels.template.text = '{name}';
                    this.amchart.legend.labels.template.truncate = true;
                    this.amchart.legend.itemContainers.template.maxWidth = this.options?.maxItemWidth || 130;
                    this.amchart.legend.itemContainers.template.paddingLeft = 10;
                    this.amchart.legend.itemContainers.template.paddingTop = 1;
                    this.amchart.legend.itemContainers.template.paddingBottom = 1;
                    this.amchart.paddingBottom = 0;
                }
                if (this.options.valueAxisRanges?.length) {
                    if (!this.amchart.legend) {
                        this.amchart.legend = new am4charts.Legend();
                    }
                    this.amchart.legend.contentAlign = 'center';
                    this.amchart.legend.valign = 'bottom';
                    this.amchart.legend.fontSize = 14;
                    this.amchart.legend.labels.template.text = '{name}';
                    this.amchart.legend.labels.template.truncate = true;
                    this.amchart.legend.markers.template.height = 2;

                    this.amchart.legend.itemContainers.template.maxWidth = 100;
                    this.amchart.legend.itemContainers.template.paddingLeft = 10;
                    this.amchart.legend.itemContainers.template.paddingTop = 1;
                    this.amchart.legend.itemContainers.template.paddingBottom = 1;
                    this.amchart.legend.itemContainers.template.clickable = false;
                    this.amchart.legend.itemContainers.template.focusable = false;
                    this.amchart.legend.itemContainers.template.width = 200;
                    this.amchart.legend.itemContainers.template.cursorOverStyle = am4core.MouseCursorStyle.default;
                    this.amchart.paddingBottom = 20;

                    this.amchart.legend.addData((this.options as DateAndValueXYChartOptionsModel).horizontalLines);
                }
                if (this.options.unit === DateAndValueXYChartUnitEnum.PROMINENCE_LEVELS) {
                    // This longer string will generally require more space than the default of 220 we gave it
                    this.amchart.legend.itemContainers.template.maxWidth = 450;
                    // If it is Prominence chart, it the series type is ReportSeriesModel and contains prominenceTrendGroup
                    let prominenceGroupKeywords;
                    if (this.chartSeries[0].prominenceTrendGroup) {
                        const prominenceTrendGroup = this.chartSeries[0].prominenceTrendGroup;
                        this.amchart.legend.labels.template.text =
                            `Prominence of [bold]${prominenceTrendGroup.name}[/] in [bold]{name}[/]`;
                        prominenceGroupKeywords = prominenceTrendGroup.keywords.toString().replace(/,/g, ', ');
                    }

                    if (prominenceGroupKeywords) {
                        this.amchart.legend.itemContainers.template.tooltip = new am4core.Tooltip();
                        this.amchart.legend.itemContainers.template.tooltipText = prominenceGroupKeywords;
                        this.amchart.legend.itemContainers.template.tooltip.label.background.fill = am4core.color(chartColors.agprBlue);
                        this.amchart.legend.itemContainers.template.tooltip.label.fill = am4core.color(chartColors.agprWhite);
                    }

                    // @HACK for some reason, if there's no data, the legend is too close to x Axis
                    if (this.options.prominenceStatus !== ProminenceRecalculationJobStatus.DONE) {
                        this.amchart.legend.dy = 10;
                    }
                }
                if (this.options.centerLegend) {
                    this.amchart.legend.contentAlign = 'center';
                    this.amchart.legend.fontSize = 14;
                }
                this.amchart.legend?.markers.template.events.on('inited', (event) => {
                    event.target.children.each(child => {
                        if (child instanceof CircleBullet) {
                            child.focusable = false;
                        }
                    });
                });
            }
        }
    }

    getCustomLegendItems(): CustomLegendItem[] {
        const items: CustomLegendItem[] = [];
        this.amchart.series.each((series) => {
            if (!series) {
                return;
            }
            const item: CustomLegendItem = {
                label: series.name,
                color: series.stroke.toString(),
            };
            items.push(item);
        });

        return items;
    }

    setUpScrollbar(): void {
        if (this.amchart) {
            this.amchart.scrollbarX = new am4core.Scrollbar();
            this.amchart.scrollbarX.dx = -20;
            this.amchart.scrollbarX.dy = -10;
            this.amchart.scrollbarX.exportable = false;

            if (!this.isSingleSeries) {
                this.amchart.scrollbarX.disabled = true;
                this.amchart.scrollbarX.focusable = false;
                this.amchart.scrollbarX.startGrip.tabindex = -1;
                this.amchart.scrollbarX.endGrip.tabindex = -1;
                this.amchart.scrollbarX.thumb.tabindex = -1;
            }
        }
    }

    setUpResponsiveness(): void {
        if (this.options.responsive) {
            this.setUpSharedReportResponsiveness();
        } else {
            this.setUpMainAppResponsiveness();
        }
    }

    setUpSharedReportResponsiveness(): void {
        this.amchart.responsive.enabled = true;
        this.amchart.svgContainer.autoResize = true;
        this.amchart.svgContainer.htmlElement.style.maxHeight = '500px';
        this.amchart.svgContainer.htmlElement.style.minHeight = '300px';
        this.amchart.chartContainer.marginTop = 25;
        this.amchart.scrollbarX.marginBottom = 25;
        this.amchart.scrollbarX.align = 'center';
        this.dateAxis.renderer.grid.template.disabled = false;
        this.dateAxis.cursorTooltipEnabled = true;
        this.valueAxes.forEach(axis => {
            axis.cursorTooltipEnabled = true;
        });
        // Cursor
        this.amchart.cursor = new am4charts.XYCursor();
        this.amchart.series.each(series => {
            series.cursorTooltipEnabled = true;
        });
        if (this.amchart.legend) {
            this.amchart.legend.dy = 20;
        }

        this.amchart.events.on('sizechanged', ev => {
            const chart = ev.target;
            if (chart.pixelWidth <= 300) {
                chart.scrollbarX.dx = -5;
                chart.scrollbarX.scale = 0.9;
            } else {
                chart.scrollbarX.dx = -25;
                chart.scrollbarX.scale = 1;
            }
            if (chart.pixelWidth <= 768) {
                chart.series.values.map(series => {
                    series.tooltip.fontSize = 14;
                });
                if (chart.legend) {
                    chart.legend.fontSize = 14;
                }
            } else {
                chart.series.values.map(series => {
                    series.tooltip.fontSize = 16;
                });
                if (chart.legend) {
                    chart.legend.fontSize = 16;
                }
            }
        });

        // Mid to large screen
        this.amchart.responsive.rules.push(
            {
                id: 'large',
                relevant: (target) => target.pixelWidth > 768,
                state: function (target, stateId) {
                    if (target instanceof am4charts.AxisRendererX) {
                        const xAxisState = target.labels.template.states.create(stateId);
                        xAxisState.properties.fontSize = 16;
                        return xAxisState;
                    }

                    if (target instanceof am4charts.AxisRendererY) {
                        const yAxisState = target.labels.template.states.create(stateId);
                        yAxisState.properties.fontSize = 16;
                        return yAxisState;
                    }

                    if (target instanceof am4charts.ValueAxis) {
                        const valueAxisState = target.states.create(stateId);
                        valueAxisState.properties.fontSize = 16;
                        return valueAxisState;
                    }

                    if (target instanceof am4charts.Cursor) {
                        const cursorState = target.states.create(stateId);
                        cursorState.properties.fontSize = 16;
                        return cursorState;
                    }

                    if (target instanceof am4charts.Series) {
                        if (target.tooltip) {
                            const seriesTooltipState = target.tooltip.states.create(stateId);
                            seriesTooltipState.properties.fontSize = 16;
                            return seriesTooltipState;
                        }
                    }
                }
            }
        );

        // Small screen
        this.amchart.responsive.rules.push(
            {
                id: 'small',
                relevant: (target) => {
                    return target.pixelWidth <= 768;
                },
                state: function (target, stateId) {
                    if (target instanceof am4charts.AxisRendererX) {
                        const xAxisState = target.labels.template.states.create(stateId);
                        xAxisState.properties.fontSize = 14;
                        return xAxisState;
                    }

                    if (target instanceof am4charts.AxisRendererY) {
                        const yAxisState = target.labels.template.states.create(stateId);
                        yAxisState.properties.fontSize = 14;
                        return yAxisState;
                    }

                    if (target instanceof am4charts.ValueAxis) {
                        const valueAxisState = target.states.create(stateId);
                        valueAxisState.properties.fontSize = 14;
                        return valueAxisState;
                    }

                    if (target instanceof am4charts.Cursor) {
                        const cursorState = target.states.create(stateId);
                        cursorState.properties.fontSize = 14;
                        return cursorState;
                    }

                    if (target instanceof am4charts.Series) {
                        if (target.tooltip) {
                            const seriesTooltipState = target.tooltip.states.create(stateId);
                            seriesTooltipState.properties.fontSize = 14;
                            return seriesTooltipState;
                        }
                    }
                }
            }
        );
    }

    setUpMainAppResponsiveness(): void {
        const smallChartWidth = ChartSizeHelper.getWidthAtMinCanvas(3, 1);
        const smallChartHeight = ChartSizeHelper.getCellHeight();
        const xsChartHeight = ChartSizeHelper.getHeight(1, 1); // for dashboard recent topic
        const largeChartWidth = ChartSizeHelper.getWidthAtMinCanvas(4, 2);
        const largeChartHeight = ChartSizeHelper.getHeight(3, 2);
        this.amchart.events.on('sizechanged', event => {
            if (this.amchart && !this.options.isSocialStyles) {
                const chart = event.target;
                if (chart.pixelWidth <= smallChartWidth || chart.pixelHeight <= smallChartHeight) {
                    this.amchart.scrollbarX.startGrip.scale = 0.75;
                    this.amchart.scrollbarX.endGrip.scale = 0.75;
                    this.dateAxis.fontSize = 12;
                    this.valueAxes.forEach(axis => axis.fontSize = 12);
                    if (this.titleLabel) {
                        this.titleLabel.marginBottom = 0;
                        if (this.header) {
                            this.header.marginBottom = 5;
                        }
                    }
                    if (this.amchart.legend) {
                        if (this.options.isStacked) {
                            this.amchart.legend.maxHeight = 70;
                            this.amchart.legend.height = 70;
                        } else {
                            this.amchart.legend.maxHeight = 32;
                        }
                        this.amchart.legend.itemContainers.template.scale = 0.9;
                    }
                    if (chart.pixelHeight <= xsChartHeight) {
                        this.amchart.paddingBottom = 10;
                        this.valueAxes.forEach(axis => {
                            axis.title.dy = -20;
                            axis.fontSize = 12;
                        });
                        if (this.amchart.legend) {
                            this.amchart.legend.maxHeight = 20;
                            this.amchart.legend.fontSize = '0.75rem';
                            this.amchart.legend.marginTop = 0;
                            this.amchart.legend.itemContainers.template.scale = 0.75;
                            this.amchart.legend.itemContainers.template.paddingTop = 0;
                            this.amchart.legend.itemContainers.template.paddingBottom = 0;
                        }
                    } else {
                        this.amchart.paddingBottom = 15;
                        this.valueAxes.forEach(axis => {
                            axis.fontSize = 12;
                        });
                        if (this.amchart.legend) {
                            this.amchart.legend.maxHeight = 54;
                            this.amchart.legend.itemContainers.template.scale = 0.9;
                            this.amchart.legend.itemContainers.template.paddingTop = 1;
                            this.amchart.legend.itemContainers.template.paddingBottom = 1;
                        }
                    }
                } else {
                    if (this.amchart.scrollbarX) {
                        this.amchart.scrollbarX.startGrip.scale = 1;
                        this.amchart.scrollbarX.endGrip.scale = 1;
                    }
                    this.dateAxis.fontSize = 13;
                    this.valueAxes.forEach(axis => axis.fontSize = 14);
                    if (this.header) {
                        this.header.marginBottom = 10;
                    }
                    this.amchart.paddingBottom = 15;
                    if (this.titleLabel) {
                        this.titleLabel.marginBottom = 5;
                    }
                    if (this.amchart.legend) {
                        this.amchart.legend.maxHeight = 500;
                        this.amchart.legend.marginTop = 0;
                        this.amchart.legend.itemContainers.template.scale = 1;
                        this.amchart.legend.itemContainers.template.paddingTop = 1;
                        this.amchart.legend.itemContainers.template.paddingBottom = 1;
                    }
                }

                if (this.amchart.legend) {
                    if (chart.pixelWidth > largeChartWidth && chart.pixelHeight > largeChartHeight) {
                        this.amchart.legend.markers.template.scale = 1;
                    } else {
                        this.amchart.legend.markers.template.scale = 0.75;
                    }
                }

                this.amchart?.legend?.afterDraw();
            }
        });
    }

    /**
     * date line charts can optionally have a set of 'events', which are just things
     * with an associated date, name, and color. they produce vertical lines in the chart,
     * which in amcharts are called 'axisRanges', and used to be called 'guides'.
     */
    handleDateAxisRange(): void {
        // todo: use base axis range, for when bar chart is needed
        const dateAxisRanges = this.options.events || [];
        for (let i = 0; i < dateAxisRanges.length; i++) {
            const dateAxisRange = dateAxisRanges[i];
            const color = dateAxisRange.color;
            const axisRange = this.dateAxis.axisRanges.create();

            // color of the grid line
            axisRange.grid.stroke = am4core.color(color);
            axisRange.grid.strokeWidth = 3.5;
            axisRange.grid.strokeOpacity = 1;

            // setup the tooltip
            const tooltip = new am4core.Tooltip();
            tooltip.getFillFromObject = false;
            tooltip.background.fill = am4core.color(color);
            tooltip.fontSize = 12;

            // setup the bullet (base of the grid line)
            axisRange.bullet = new am4core.Circle();
            axisRange.bullet.width = 6;
            axisRange.bullet.height = 6;
            if (!this.options?.ignoreBullets) {
                axisRange.bullet.fill = am4core.color(chartColors.agprWhite);
                axisRange.bullet.stroke = am4core.color(color);
                axisRange.bullet.strokeWidth = 1;
            } else {
                axisRange.bullet.strokeWidth = 5;
                axisRange.bullet.fill = am4core.color(color);
                axisRange.bullet.strokeOpacity = 0;
            }

            let tooltipText = `<center style="font-size: 0.875rem; padding: 3px 0 5px 0;">${dateAxisRange.name}</center>`;
            if (dateAxisRange.date) {
                axisRange.date = new Date(dateAxisRange.date.absoluteDate);
                tooltipText += `<center style="font-size: 0.875rem; padding-bottom: 5px;">${axisRange.date.toDateString()}</center>`;
            }

            if (dateAxisRange.typeLabel) {
                tooltipText += `<center style="font-size: 0.875rem; padding-bottom: 3px;">${dateAxisRange.typeLabel}</center>`;
            }

            axisRange.grid.tooltipHTML = tooltipText;
            axisRange.grid.tooltip = tooltip;
            axisRange.bullet.tooltipHTML = tooltipText;
            axisRange.bullet.tooltip = tooltip;
        }
    }

    handleValueAxisRange(): void {
        this.options.valueAxisRanges?.forEach(range => {
            this.addAxisLine(
                this.valueAxes[0],
                range,
                chartSpecificTypesToValueAxisDirectionsMap.get(this.options.chartSpecificType) === 'yAxes'
            );
        });
    }

    // For sparkline chart - disable most elements
    handleSparkline(): void {
        if (this.options.isSparkline) {
            this.amchart.svgContainer.autoResize = this.options.autoResize;
            this.amchart.maskBullets = false;
            this.amchart.scrollbarX.disabled = true;
            this.amchart.scrollbarX.visible = false;
            this.amchart.zoomOutButton.disabled = true;
            this.amchart.focusable = false;

            this.dateAxis.renderer.grid.template.disabled = true;
            this.dateAxis.renderer.labels.template.disabled = true;
            this.dateAxis.renderer.ticks.template.disabled = true;
            if (this.amchart?.cursor) {
                this.amchart.cursor.disabled = true;
            }
            this.dateAxis.cursorTooltipEnabled = false;

            this.valueAxes.forEach(axis => {
                axis.extraMax = 0.05;
                axis.extraMin = 0;
                axis.renderer.grid.template.disabled = true;
                axis.renderer.baseGrid.disabled = true;
                axis.renderer.labels.template.disabled = true;
                axis.renderer.minGridDistance = 1;
                axis.cursorTooltipEnabled = false;
                axis.paddingTop = -10;
            });
        }
    }

    public updateData(data: DataPointModel[][]): void {
        this.rawData = data;
        if (!this.amchart) {
            return;
        }
        this.amchart.data = JSON.parse(JSON.stringify(data[0]));

        if (!data?.includes(null) && this.isContentEmpty()) {
            data = null;
            this.dateAxis.disabled = true;
            this.valueAxes[0].disabled = true;
            this.valueAxes.forEach(axis => axis.disabled = true);
            this.amchart.scrollbarX.disabled = true;
            if (this.amseries) {
                this.amseries.disabled = true;
            }
            this.showNoContentMessage();
            this.noContentLabel.dom.style.display = 'block';
            this.amchart?.legend?.hide(0);
            this.dateAxis?.axisRanges?.each(range => range.hide());
            this.valueAxes[0].axisRanges?.each(range => range.hide());
        } else {
            this.dateAxis.disabled = false;
            this.valueAxes.forEach(axis => axis.disabled = false);
            if (this.noContentLabel) {
                this.noContentLabel.dom.style.display = 'none';
                this.noContentLabel.text = '';
            }
            this.amchart?.legend?.show(0);
            this.dateAxis?.axisRanges?.each(range => range.show());
            this.valueAxes[0].axisRanges?.each(range => range.show());
        }

        if (!data) {
            this.amchart.data = null;
            this.ready.next(true);
            return;
        }

        // add negative scale for curve to be displayed
        if (this.options.isSparkline) {
            const max = this.amchart.data.reduce((x, y) => {
                let maximum = x;
                for (let i = 0; i < this.chartSeries.length; i++) {
                    maximum = Math.max(maximum, (y['value' + i] || 0));
                }
                return maximum;
            }, 0);
            this.valueAxes.forEach(x => x.min = -Math.max(1, (max / 2)));
        }

        this.updateSeriesStyles();
        if (!this.options.doReadyInInit) {
            this.ready.next(true);
            this.resetZoomOut();
        }
    }


    public updateSeriesStyles(): void {}

    public export(type: keyof am4core.IExportOptions, filename?: string): Promise<boolean> {
        const dataFields = {};
        dataFields['label'] = this.options.label || 'Label' ;
        if (this.options.startTimeLabel) {
            dataFields['startTimeExport'] = this.options.startTimeLabel;
        }
        if (this.options.endTimeLabel) {
            dataFields['endTimeExport'] = this.options.endTimeLabel;
        }

        if (this.options.unit === DateAndValueXYChartUnitEnum.PROMINENCE_LEVELS){
            dataFields['mentionCount'] = this.options.mentionsLabel;
            // for prominence chart, yValue0 is average prominence value, and the label is 'average prominence'
            // dataFields['yValue0'] = this.options.averageProminenceLabel;
        } else {
            // if it is not prominence chart, the label for yValue should be its series label name + metric label
            let series;
            if (this.options.isStacked) {
                series = this.chartSeries.reverse();
            } else {
                series = this.chartSeries;
            }
            series.forEach((serie, index) => {
                dataFields[`value${index}`] = this.options.isStacked ? serie.label + ' (count)' : serie.exportLabel;
            });
            if (this.options.isStacked) {
                series.forEach((serie, index) => {
                    dataFields[`percent${index}`] = serie.label + ' (percent)';
                });
            }
        }

        if (this.options.events?.length > 0) {
            dataFields['prEventName'] =  this.options.eventNameLabel;
            dataFields['prEventType'] =  this.options.eventTypeLabel;
            dataFields['prEventDate'] =  this.options.eventDateLabel;
        }

        const exportType = this.exportTypes.find(exportOption => exportOption === type);
        // have to have manually export to properly convert the dates to ignore the minutes
        if (exportType) {
            const useGroupDataPoints = this.options.groupIntervals?.length === 1 && (
                this.options.groupIntervals[0].timeUnit === 'week'
                || this.options.groupIntervals[0].timeUnit === 'month'
                || this.options.groupIntervals[0].timeUnit === 'year'
            );

            if (useGroupDataPoints && this.groupDataPoints?.length) {
                manualChartExport.exportFlatSeriesData(exportType, this.groupDataPoints, dataFields, filename);
            } else {
                manualChartExport.exportFlatSeriesData(exportType, this.amchart.data, dataFields, filename);
            }

            return;
        }

        this.amchart.exporting.filePrefix = filename;
        this.amchart.exporting.useWebFonts = false;

        return this.amchart.exporting.export(type);
    }

    /**
     * Modifies the chart to have the right styles and be the right dimensions for export
     */
    public setExportStyles(scale?: number, keepFontsize?: boolean): void {
        super.setExportStyles(scale, keepFontsize);

        if (this.amchart.scrollbarX) {
            this.amchart.scrollbarX.visible = false;
        }
        this.amchart.zoomOutButton.visible = false;
        if (this.primarySeriesLabel) {
            this.primarySeriesLabel.fontSize = 16;
        }
        if (this.amchart.legend) {
            this.amchart.legend.itemContainers.template.width = 180;
        }
        const legendItemWidth = this.options.styles['legendItemWidth'];
        if (legendItemWidth && this.amchart.legend) {
            this.amchart.legend.itemContainers.template.width = legendItemWidth;
            this.amchart.legend.labels.template.truncate = true;
        }
    }

    /**
     * Undoes the changes made in setExportStyles to make the chart normal again
     */
    public unsetExportStyles(): void {
        if (!this.amchart) {
            return;
        }
        super.unsetExportStyles();
        if (this.amchart.scrollbarX) {
            if (this.isSingleSeries) {
                this.amchart.scrollbarX.visible = true;
                this.amchart.zoomOutButton.visible = true;
            } else {
                this.amchart.scrollbarX.visible = false;
            }
        }
        if (this.primarySeriesLabel) {
            this.primarySeriesLabel.fontSize = '0.781rem';
        }
        if (this.amchart.legend) {
            this.amchart.legend.itemContainers.template.width = 120;
        }
    }

    public isContentEmpty(): boolean {
        if (!this.rawData) {
            return;
        }
        for (const value of this.rawData) {
            if (value && value.length > 0) {
                return false;
            }
        }

        return true;
    }

    public clone(): DateValueXYChart {
        return new DateValueXYChart(
            this.series.map(s => s.clone ? s.clone() : s),
            this.options,
            this.metadata,
            this.chartSeries
        );
    }

    // HACK for some unknown reason, initially load the chart, the chart is zoomed in, here reset it to default
    private resetZoomOut() {
        setTimeout(() => {
            this.amchart?.zoomOutButton.dispatchImmediately('hit');
        }, 100);
    }
}
